/**
 * **La gestion des guichets** — tous les guichets du réseau, et leurs gestes (V90,
 * 2026-10-01).
 *
 * Demandé par le porteur : « l'écran Réseau guichets doit pouvoir ajouter, et toutes
 * les actions ». Jusqu'ici un guichet s'ouvrait à la main en SQL, sans trace, et ne se
 * suspendait ni ne se fermait nulle part.
 *
 * - **Ajouter** un guichet : son code, sa fiche. Le serveur ouvre en même temps son
 *   compte au grand livre, À ZÉRO — alimenter un guichet n'est pas un champ de
 *   formulaire.
 * - **Modifier** sa fiche, préremplie de ce que le serveur sert.
 * - **Suspendre**, **réactiver**, **fermer** — en une signature depuis la V91.
 *   Si la matrice remettait une double validation, l'issue le dirait, et rien ne
 *   serait annoncé comme fait qui ne l'est pas.
 * - **Comptes** : les guichetiers du guichet, dans `OutletOperatorsPanel`.
 *
 * Cette section a sa PROPRE lecture et sa propre erreur : un rôle qui gère les
 * guichets sans voir la liquidité — ou l'inverse — garde la moitié qui lui revient.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import type { ActionOutcome } from '../http/liquidity.js';
import {
  changeOutletStatus,
  createOutlet,
  descriptionOf,
  fetchNetworkOutlets,
  outletDescriptionFields,
  updateOutlet,
} from '../http/outlets.js';
import type {
  NetworkOutlet,
  OutletDescription,
  OutletStatusGesture,
} from '../http/outlets.js';
import { OutletOperatorsPanel } from './OutletOperatorsPanel.js';

/** Le geste en cours. */
type Geste =
  | { readonly kind: 'create' }
  | { readonly kind: 'update'; readonly outlet: NetworkOutlet }
  | {
      readonly kind: 'status';
      readonly outlet: NetworkOutlet;
      readonly gesture: OutletStatusGesture;
    };

/** Les champs de la fiche obligatoires — les deux que la base exige. */
const OBLIGATOIRES: ReadonlySet<string> = new Set(['businessName', 'category']);

/** Les gestes de statut qu'un guichet offre, selon son statut. */
function gestesDeStatut(status: string): readonly OutletStatusGesture[] {
  switch (status) {
    case 'ACTIF':
      return ['suspend', 'close'];
    case 'SUSPENDU':
      return ['reactivate', 'close'];
    default:
      return [];
  }
}

/** La section de gestion des guichets. */
export function OutletManagement(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();

  const [guichets, setGuichets] = useState<readonly NetworkOutlet[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [geste, setGeste] = useState<Geste | null>(null);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [comptesDe, setComptesDe] = useState<NetworkOutlet | null>(null);

  const relire = useCallback(async (): Promise<void> => {
    try {
      setGuichets(await fetchNetworkOutlets(client));
      setErreur(null);
    } catch (echec) {
      setGuichets(null);
      setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client]);

  useEffect(() => {
    void relire();
  }, [relire]);

  const champsDeLaFiche: readonly FormFieldDescriptor[] = useMemo(
    () =>
      outletDescriptionFields.map((nom) => ({
        name: nom,
        labelKey: `outletNetwork.manage.fields.${nom}`,
        control: 'text' as const,
        required: OBLIGATOIRES.has(nom),
      })),
    [],
  );

  const motif: FormFieldDescriptor = useMemo(
    () => ({
      name: 'reason',
      labelKey: 'outletNetwork.manage.fields.reason',
      control: 'text',
      required: true,
    }),
    [],
  );

  const champs: readonly FormFieldDescriptor[] = useMemo(() => {
    if (geste === null) return [];
    if (geste.kind === 'create') {
      return [
        {
          name: 'outletCode',
          labelKey: 'outletNetwork.manage.fields.outletCode',
          placeholderKey: 'outletNetwork.manage.fields.outletCodePlaceholder',
          control: 'text',
          required: true,
        },
        ...champsDeLaFiche,
        motif,
      ];
    }
    if (geste.kind === 'update') return [...champsDeLaFiche, motif];
    return [motif];
  }, [geste, champsDeLaFiche, motif]);

  // Les valeurs préremplies d'une modification — gardées STABLES tant que le
  // geste ne change pas : `FormShell` réécrit le formulaire à chaque nouvelle
  // référence, et effacerait la saisie en cours.
  const valeurs = useMemo(
    () =>
      geste?.kind === 'update'
        ? { ...descriptionOf(geste.outlet), reason: '' }
        : undefined,
    [geste],
  );

  const executer = useCallback(
    async (saisie: Readonly<Record<string, string>>): Promise<void> => {
      if (geste === null) return;
      const raison = saisie.reason ?? '';
      const fiche = Object.fromEntries(
        outletDescriptionFields.map((nom) => [nom, saisie[nom] ?? '']),
      ) as unknown as OutletDescription;
      try {
        const issue: ActionOutcome =
          geste.kind === 'create'
            ? await createOutlet(client, saisie.outletCode ?? '', fiche, raison)
            : geste.kind === 'update'
              ? await updateOutlet(client, geste.outlet.outletCode, fiche, raison)
              : await changeOutletStatus(
                  client,
                  geste.outlet.outletCode,
                  geste.gesture,
                  raison,
                );
        setErreurGeste(null);
        setGeste(null);
        // Une demande en attente n'est JAMAIS annoncée comme faite : le guichet
        // n'a pas encore changé d'état.
        setAvis(
          issue.outcome === 'EXECUTED'
            ? (issue.detail ?? t('outletNetwork.operators.done'))
            : t('outletNetwork.operators.pending', { id: issue.approvalRequestId ?? '' }),
        );
        await relire();
      } catch (echec) {
        setErreurGeste(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client, geste, relire, t],
  );

  const ouvrir = (suivant: Geste): void => {
    setGeste(suivant);
    setErreurGeste(null);
    setAvis(null);
  };

  return (
    <section
      aria-label={t('outletNetwork.manage.landmark')}
      className="outlet-network__operators"
      data-testid="outlet-management"
    >
      <h2 className="outlet-network__section-title">{t('outletNetwork.manage.title')}</h2>
      <p className="outlet-network__meta">{t('outletNetwork.manage.notice')}</p>

      {avis === null ? null : (
        <p className="outlet-network__avis" role="status">
          {avis}
        </p>
      )}
      {erreur === null ? null : (
        <p className="outlet-network__notice" role="alert">
          {t(erreur)}
        </p>
      )}

      {guichets === null ? (
        erreur === null ? (
          <p className="outlet-network__idle">{t('outletNetwork.loading')}</p>
        ) : null
      ) : guichets.length === 0 ? (
        <p className="outlet-network__idle" data-testid="outlet-management-empty">
          {t('outletNetwork.manage.empty')}
        </p>
      ) : (
        <div className="outlet-network__table-frame">
          <table className="outlet-network__table" data-testid="outlet-management-table">
            <thead>
              <tr>
                <th scope="col">{t('outletNetwork.manage.columns.outlet')}</th>
                <th scope="col">{t('outletNetwork.manage.columns.category')}</th>
                <th scope="col">{t('outletNetwork.manage.columns.region')}</th>
                <th scope="col">{t('outletNetwork.manage.columns.status')}</th>
                <th scope="col">{t('outletNetwork.manage.columns.operators')}</th>
                <th scope="col">{t('outletNetwork.manage.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {guichets.map((guichet) => (
                <tr data-testid={`managed-outlet-${guichet.outletCode}`} key={guichet.outletId}>
                  <td>
                    <span className="outlet-network__outlet-code">{guichet.outletCode}</span>
                    <span className="outlet-network__outlet-name">{guichet.businessName}</span>
                  </td>
                  <td>{guichet.category}</td>
                  <td>{guichet.region ?? t('outletNetwork.outlets.unknown')}</td>
                  {/* L'état se dit par le MOT, jamais par la seule teinte. */}
                  <td>
                    <span
                      className="outlet-network__state-badge"
                      data-tone={tonDuStatut(guichet.status)}
                    >
                      {t(`outletNetwork.manage.statuses.${guichet.status}`, guichet.status)}
                    </span>
                  </td>
                  <td data-emphasis="numeric">{guichet.activeOperators}</td>
                  <td className="outlet-network__operator-actions">
                    <button
                      aria-label={t('outletNetwork.outlets.accountsOf', {
                        code: guichet.outletCode,
                      })}
                      className="outlet-network__button"
                      data-testid={`outlet-accounts-${guichet.outletCode}`}
                      onClick={() => {
                        setComptesDe(guichet);
                      }}
                      type="button"
                    >
                      {t('outletNetwork.outlets.accounts')}
                    </button>
                    {guichet.status === 'FERME' ? null : (
                      <button
                        className="outlet-network__button"
                        onClick={() => {
                          ouvrir({ kind: 'update', outlet: guichet });
                        }}
                        type="button"
                      >
                        {t('outletNetwork.manage.gestures.update')}
                      </button>
                    )}
                    {gestesDeStatut(guichet.status).map((gesture) => (
                      <button
                        className="outlet-network__button"
                        data-variant={gesture === 'close' ? 'danger' : undefined}
                        key={gesture}
                        onClick={() => {
                          ouvrir({ kind: 'status', outlet: guichet, gesture });
                        }}
                        type="button"
                      >
                        {t(`outletNetwork.manage.gestures.${gesture}`)}
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {geste === null ? (
        <button
          className="outlet-network__button"
          data-variant="primary"
          onClick={() => {
            ouvrir({ kind: 'create' });
          }}
          type="button"
        >
          {t('outletNetwork.manage.gestures.create')}
        </button>
      ) : (
        <div className="outlet-network__operator-form" data-testid="outlet-management-form">
          <h3 className="outlet-network__section-title">
            {geste.kind === 'create'
              ? t('outletNetwork.manage.titles.create')
              : t(
                  `outletNetwork.manage.titles.${
                    geste.kind === 'update' ? 'update' : geste.gesture
                  }`,
                  { code: geste.outlet.outletCode },
                )}
          </h3>
          {geste.kind === 'status' ? (
            <p className="outlet-network__meta">
              {t(`outletNetwork.manage.consequences.${geste.gesture}`)}
            </p>
          ) : null}
          {erreurGeste === null ? null : (
            <p className="outlet-network__notice" role="alert">
              {t(erreurGeste)}
            </p>
          )}
          <FormShell
            fields={champs}
            layout="stacked"
            onSubmit={executer}
            submitLabelKey={`outletNetwork.manage.submits.${
              geste.kind === 'status' ? geste.gesture : geste.kind
            }`}
            {...(valeurs === undefined ? {} : { values: valeurs })}
          />
          <button
            className="outlet-network__button"
            onClick={() => {
              setGeste(null);
              setErreurGeste(null);
            }}
            type="button"
          >
            {t('outletNetwork.operators.cancel')}
          </button>
        </div>
      )}

      {comptesDe === null ? null : (
        <OutletOperatorsPanel
          outlet={{
            outletId: comptesDe.outletId,
            outletCode: comptesDe.outletCode,
            businessName: comptesDe.businessName,
          }}
        />
      )}
    </section>
  );
}

/** La teinte qui double le mot du statut. */
function tonDuStatut(status: string): 'success' | 'warning' | 'danger' {
  if (status === 'ACTIF') return 'success';
  if (status === 'SUSPENDU') return 'warning';
  return 'danger';
}
